import { useEffect } from "react";
import { createRouter as createTanStackRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";
import { reportLovableError } from "./lib/lovable-error-reporting";
import { applyPortalTheme, getPortalTheme } from "./lib/portalTheme";

/** Antes ningún error de render/loader tenía una pantalla de rescate — un componente que
 *  lanzaba una excepción dejaba la pantalla en blanco sin ningún mensaje ni forma de
 *  recuperarse salvo recargar a mano (hallazgo de auditoría real: la app no tenía ningún
 *  ErrorBoundary). Esto es el equivalente de TanStack Router a un ErrorBoundary global. */
function DefaultErrorComponent({ error }: ErrorComponentProps) {
  useEffect(() => { reportLovableError(error, { source: "router.defaultErrorComponent" }); }, [error]);
  // Al fallar una pantalla del Portal o de un programa, su raíz se desmonta y el atributo del modo claro se
  // pierde: se vuelve a poner para que el aviso respete el modo elegido (staff y sitio público quedan oscuros).
  useEffect(() => {
    const p = window.location.pathname;
    const enPortal = ((p === "/portal" || p.startsWith("/portal/")) && !p.startsWith("/portal/hoja-de-ruta")) || p.startsWith("/mi-programa/");
    if (!enPortal) return;
    applyPortalTheme(getPortalTheme());
    return () => applyPortalTheme(null);
  }, []);
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--cp-bg, #0a0a0c)", color: "var(--cp-tx, #f2f3f5)", padding: 24, textAlign: "center" }}>
      <div style={{ maxWidth: 420 }}>
        <div style={{ fontSize: 30, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Algo salió mal</div>
        <div style={{ fontSize: 13, color: "var(--cp-mu, #9a9ca3)", marginBottom: 20, lineHeight: 1.5 }}>
          Hubo un error inesperado en esta pantalla. Recarga la página — si el problema sigue, avísale al equipo.
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: "10px 20px", fontSize: 13.5, fontWeight: 700, borderRadius: 8, border: "none", background: "#22d3ee", color: "#06232b", cursor: "pointer" }}
        >
          Recargar
        </button>
      </div>
    </div>
  );
}

export function createRouter(queryClient: QueryClient) {
  return createTanStackRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    scrollRestoration: true,
    defaultErrorComponent: DefaultErrorComponent,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
